import { getCurrentTimeHHMM, isTimeInRange } from './timeUtils'

export interface DeliveryRule {
  id?: string | number
  min_order_amount?: number
  max_order_amount?: number | null
  price_range_from?: number
  price_range_to?: number | null
  delivery_cost: number
  from_time: string
  to_time: string
}

export interface DeliveryZone {
  id: string | number
  name?: string
  min_order_limit?: number
  rules?: DeliveryRule[]
}

export function findMatchingDeliveryRule(
  rules: DeliveryRule[],
  orderAmount: number,
  currentTime?: string,
): DeliveryRule | null {
  if (!Array.isArray(rules) || rules.length === 0) return null

  const time = currentTime || getCurrentTimeHHMM()

  const matchingRule = rules.find((rule) => {
    const min = Number(rule.min_order_amount ?? rule.price_range_from) || 0
    const maxValue = rule.max_order_amount ?? rule.price_range_to
    const max = maxValue == null ? Infinity : Number(maxValue)

    // Границы как на бэке (branch.service.ts → findApplicableDeliveryRule):
    // нижняя включительно, верхняя ТОЖЕ включительно (<= max), иначе фронт и
    // бэк расходятся ровно на граничной сумме.
    const inAmount = orderAmount >= min && orderAmount <= max

    const inTime = isTimeInRange(time, rule.from_time, rule.to_time)
    return inAmount && inTime
  })

  // ВАЖНО: если ни одно правило не подошло (по времени/сумме) — НЕ берём rules[0].
  // Бэкенд в этом случае отдаёт бесплатную доставку (deliveryRules.deliveryCost = 0,
  // см. branch.service.impl.ts). Возврат rules[0] давал ложные 400₽ в сводке корзины
  // при том, что заказ реально оформлялся с доставкой 0₽.
  return matchingRule || null
}

export function calculateDeliveryCost(
  deliveryZone: DeliveryZone | null,
  orderAmount: number,
  isDelivery: boolean,
): number {
  if (!isDelivery || !deliveryZone?.rules) return 0
  const selectedRule = findMatchingDeliveryRule(deliveryZone.rules, orderAmount)
  return selectedRule ? selectedRule.delivery_cost : 0
}

export function getMinOrderAmount(deliveryZone: DeliveryZone | null): number {
  return deliveryZone?.min_order_limit || 0
}

export function isMinOrderAmountReached(
  cartAmount: number,
  deliveryZone: DeliveryZone | null,
  isDelivery: boolean,
): boolean {
  if (!isDelivery) return true
  const minAmount = getMinOrderAmount(deliveryZone)
  return cartAmount >= minAmount
}

export function findFreeDeliveryRule(rules: DeliveryRule[]): DeliveryRule | null {
  if (!Array.isArray(rules)) return null
  return rules.find(rule => rule.delivery_cost === 0) || null
}

export function calculateToFreeDelivery(rules: DeliveryRule[], cartAmount: number): number | null {
  const freeRule = findFreeDeliveryRule(rules) as any
  if (!freeRule) return null
  const requiredAmount = freeRule.min_order_amount ?? freeRule.price_range_from ?? 0
  return Math.max(requiredAmount - cartAmount, 0)
}

export function isFreeDeliveryAvailable(rules: DeliveryRule[]): boolean {
  return findFreeDeliveryRule(rules) !== null
}

export function getDeliveryTimeSlots(rules: DeliveryRule[]): Array<{ from: string; to: string }> {
  if (!Array.isArray(rules)) return []
  return rules.map(rule => ({ from: rule.from_time, to: rule.to_time }))
}

export function formatDeliveryInfo(deliveryZone: DeliveryZone | null, orderAmount: number) {
  if (!deliveryZone) {
    return { cost: 0, minAmount: 0, toMinOrder: 0, toFreeDelivery: null, isFreeAvailable: false }
  }
  const cost = calculateDeliveryCost(deliveryZone, orderAmount, true)
  const minAmount = getMinOrderAmount(deliveryZone)
  const toMinOrder = Math.max(minAmount - orderAmount, 0)
  const toFreeDelivery = calculateToFreeDelivery(deliveryZone.rules || [], orderAmount)
  const isFreeAvailable = isFreeDeliveryAvailable(deliveryZone.rules || [])
  return { cost, minAmount, toMinOrder, toFreeDelivery, isFreeAvailable }
}
